/**
 * Records → FILES API client.
 *
 * Builds on adminFetch rather than re-implementing auth: that helper already
 * carries the Supabase token, the X-Supabase-Environment header used for
 * prod/demo routing, and the 401 → signOut behaviour. Duplicating it here would
 * mean a second place to forget the environment header.
 */
import { adminFetch, getAuthHeaders, API_BASE_URL } from './adminApi';

/** Filename from Content-Disposition, so a ZIP keeps the name the server chose. */
function filenameFrom(response: Response): string | null {
  const cd = response.headers.get('content-disposition') ?? '';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  return m ? decodeURIComponent(m[1]) : null;
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    return body.message || body.error || `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
}
import type {
  DocumentHistoryEntry,
  DocumentListResponse,
  MatrixResponse,
  RequiredDocumentSlot,
  UnitDocument,
  UnitDocumentsResponse,
} from '@/types/documents';

/**
 * Build a query string, omitting empty filters entirely.
 *
 * An EMPTY id list must not be sent as an empty param: the API fails closed on
 * an explicitly empty filter (returns nothing) but treats an ABSENT filter as
 * unfiltered. Sending `company_ids=` would silently return zero rows.
 */
function qs(params: Record<string, string | string[] | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      search.set(key, value.join(','));
    } else if (value !== '') {
      search.set(key, value);
    }
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

export interface MatrixFilters {
  companyIds?: string[];
  groupIds?: string[];
}

/** The units × types grid backing the default view. */
export async function fetchDocumentMatrix(filters: MatrixFilters = {}): Promise<MatrixResponse> {
  return adminFetch<MatrixResponse>(
    `/documents/matrix${qs({ company_ids: filters.companyIds, group_ids: filters.groupIds })}`,
  );
}

/** #213: a gap on a booked trailer (a required document clients see, missing or expired). */
export interface BookedGap {
  key: string;
  label: string;
  typeId: string;
  expirationDate?: string | null;
  /**
   * Expired: the document an upload replaces. Missing certificate of insurance (#260): one on file
   * that can't fill the gap yet, per `reason`.
   */
  documentId?: string;
  /** #260, missing certificates only: why the one on file doesn't count for this booking yet. */
  reason?: 'not_attached' | 'other_booking';
}
export interface BookedGapsResponse {
  rows: {
    unitId: string; unitCode: string; bookingFrom: string; bookingTo: string | null;
    /** #260: the booking's production, from Sales; null when unknown or when the booking has none yet. */
    production?: { groupId: string; name: string | null } | null;
    /** #260: the Sales booking, when known (a certificate can be filed on the booking itself). */
    rentalId?: string | null;
    missing: BookedGap[]; expired: BookedGap[];
  }[];
  /** Trailers on an active or upcoming booking. */
  trailers: number;
  checkedAt: string;
}

/** Booked trailers missing a document clients see, soonest booking first (staff only). */
export async function fetchBookedGaps(): Promise<BookedGapsResponse> {
  return adminFetch<BookedGapsResponse>('/documents/booked-gaps');
}

/**
 * #260 part 3: attach a certificate of insurance on file to a booking: its production, or the booking
 * itself when Sales hasn't given it one yet (groupId null). The server checks the booking includes
 * the trailer, and (only_if_unattached) refuses a certificate a colleague attached meanwhile; a
 * refusal throws with its message.
 */
export async function attachCertificate(documentId: string, target: { groupId: string | null; rentalId: string | null }): Promise<void> {
  await adminFetch<unknown>(`/documents/${encodeURIComponent(documentId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ group_id: target.groupId, rental_id: target.rentalId, only_if_unattached: true }),
  });
}

export interface DocumentListFilters extends MatrixFilters {
  unitIds?: string[];
  typeKeys?: string[];
  status?: string;
  limit?: number;
}

/**
 * Raw row as GET /admin/documents actually returns it: snake_case, with the
 * document_types and units joins nested rather than flattened.
 */
interface RawDocumentRow {
  id: string;
  unit_id: string;
  company_id: string | null;
  document_type_id: string;
  document_type_key: string;
  custom_type_label: string | null;
  expiration_date: string | null;
  file_name: string;
  mime_type: string | null;
  page_count?: number | null;
  file_size_bytes: number;
  uploaded_at: string;
  verification_status: string;
  superseded_by_id: string | null;
  status: UnitDocument['status'];
  document_types?: { key?: string; label?: string } | null;
  units?: { unit_code?: string; name?: string; company_id?: string } | null;
}

/**
 * Map a raw row onto the flat shape the UI consumes.
 *
 * The list endpoint returns rows straight from PostgREST — it does NOT return
 * the camelCase DTO the matrix endpoint builds by hand. Consuming it directly
 * left every grouping key, filename and unit code undefined in the panels.
 * Joins are optional, so each read falls back rather than throwing.
 */
export function normalizeDocument(raw: RawDocumentRow): UnitDocument {
  return {
    id: raw.id,
    unitId: raw.unit_id,
    unitCode: raw.units?.unit_code ?? null,
    companyId: raw.company_id ?? raw.units?.company_id ?? null,
    // The list endpoint does not join company or group names — only the matrix
    // carries them. enrichWithScope() fills these in from matrix rows; leaving
    // them null here is honest about what this response actually contains.
    companyName: null,
    groupName: null,
    documentTypeId: raw.document_type_id,
    documentTypeKey: raw.document_types?.key ?? raw.document_type_key,
    // An 'other' document's meaning lives in its free-text label; showing the
    // generic "Other" for it makes every such row indistinguishable.
    documentTypeLabel:
      raw.document_type_key === 'other' && raw.custom_type_label
        ? raw.custom_type_label
        : raw.document_types?.label ?? null,
    otherLabel: raw.custom_type_label ?? null,
    fileName: raw.file_name,
    fileSizeBytes: raw.file_size_bytes ?? 0,
    mimeType: raw.mime_type ?? null,
    pageCount: raw.page_count ?? null,
    expirationDate: raw.expiration_date ?? null,
    uploadedAt: raw.uploaded_at,
    verificationStatus: raw.verification_status,
    supersededById: raw.superseded_by_id ?? null,
    status: raw.status,
  };
}

/** Flat document list, used by the By type / By unit panels. */
export async function fetchDocuments(filters: DocumentListFilters = {}): Promise<DocumentListResponse> {
  const response = await adminFetch<{ documents?: RawDocumentRow[]; total?: number }>(
    `/documents${qs({
      company_ids: filters.companyIds,
      group_ids: filters.groupIds,
      unit_ids: filters.unitIds,
      type_keys: filters.typeKeys,
      status: filters.status,
      limit: filters.limit ? String(filters.limit) : undefined,
    })}`,
  );
  return {
    documents: (response.documents ?? []).map(normalizeDocument),
    total: response.total,
  };
}

/**
 * One unit's required-type strip plus its documents (unit route).
 *
 * `documents` comes back as RAW rows, exactly like the list endpoint, so it is
 * normalised here. Consuming it directly is the mistake that left the By-unit
 * panels rendering undefined filenames.
 */
export async function fetchUnitDocuments(unitId: string): Promise<UnitDocumentsResponse> {
  const raw = await adminFetch<{
    unit: UnitDocumentsResponse['unit'];
    requiredDocuments: RequiredDocumentSlot[];
    documents?: RawDocumentRow[];
    overallStatus: UnitDocumentsResponse['overallStatus'];
  }>(`/documents/units/${unitId}`);

  return {
    unit: raw.unit,
    requiredDocuments: raw.requiredDocuments ?? [],
    documents: (raw.documents ?? []).map(normalizeDocument),
    overallStatus: raw.overallStatus,
  };
}

/**
 * Supersession chain for a document, newest first.
 *
 * `truncated` is surfaced rather than hidden: a chain longer than the server
 * will walk is rare, but silently showing a partial history of a compliance
 * document is worse than saying it is partial.
 */
export async function fetchDocumentHistory(
  documentId: string,
): Promise<{ history: DocumentHistoryEntry[]; truncated: boolean }> {
  const raw = await adminFetch<{
    history?: Record<string, unknown>[];
    truncated?: boolean;
  }>(`/documents/${documentId}/history`);

  return {
    history: (raw.history ?? []).map((h) => ({
      id: String(h.id),
      fileName: String(h.file_name ?? h.fileName ?? ''),
      uploadedAt: String(h.uploaded_at ?? h.uploadedAt ?? ''),
      expirationDate: (h.expiration_date ?? h.expirationDate ?? null) as string | null,
      verificationStatus: String(h.verification_status ?? h.verificationStatus ?? ''),
      supersededById: (h.superseded_by_id ?? h.supersededById ?? null) as string | null,
      fileSizeBytes: Number(h.file_size_bytes ?? h.fileSizeBytes ?? 0),
    })),
    truncated: Boolean(raw.truncated),
  };
}

/**
 * Signed-URL cache.
 *
 * Keyed on (documentId, kind) — NOT documentId alone. The API issues two
 * different URLs per document: preview is inline, download carries a
 * Content-Disposition restoring the original filename. Caching them under one
 * key would hand a preview URL to the download button and open the file in a
 * tab instead of saving it.
 *
 * Entries are dropped shortly before the server-side expiry so a cached URL is
 * never handed out dead.
 */
type UrlKind = 'preview' | 'download';

interface CacheEntry {
  url: string;
  expiresAt: number;
}

/**
 * Held below the server's SIGN_SECONDS (600) with real headroom, so a URL
 * handed out at the last moment still has time to paint a slow PDF.
 */
export const SIGNED_URL_TTL_MS = 480 * 1000;
const urlCache = new Map<string, CacheEntry>();

function cacheKey(documentId: string, kind: UrlKind): string {
  return `${documentId}::${kind}`;
}

export async function getSignedUrl(documentId: string, kind: UrlKind = 'preview'): Promise<string> {
  const key = cacheKey(documentId, kind);
  const hit = urlCache.get(key);
  if (hit && hit.expiresAt > Date.now()) return hit.url;

  const { url } = await adminFetch<{ url: string }>(`/documents/${documentId}/${kind}`);
  urlCache.set(key, { url, expiresAt: Date.now() + SIGNED_URL_TTL_MS });
  return url;
}

/** Synchronous peek, so an iframe can paint on first render when already warm. */
export function peekSignedUrl(documentId: string, kind: UrlKind = 'preview'): string | null {
  const hit = urlCache.get(cacheKey(documentId, kind));
  return hit && hit.expiresAt > Date.now() ? hit.url : null;
}

/** Exposed for tests and for invalidating after a replace. */
export function clearSignedUrlCache(documentId?: string): void {
  if (!documentId) {
    urlCache.clear();
    return;
  }
  urlCache.delete(cacheKey(documentId, 'preview'));
  urlCache.delete(cacheKey(documentId, 'download'));
}

/**
 * Fill in company/group names the list endpoint does not return.
 *
 * Without this the By-unit panels rendered an empty subtitle for every unit:
 * the fields exist on the DTO but only the matrix response populates them.
 */
export function enrichWithScope(
  documents: UnitDocument[],
  rows: { unitId: string; companyName: string | null; groupName: string | null }[],
): UnitDocument[] {
  if (rows.length === 0) return documents;
  const byUnit = new Map(rows.map((r) => [r.unitId, r]));
  return documents.map((d) => {
    const row = byUnit.get(d.unitId);
    return row ? { ...d, companyName: row.companyName, groupName: row.groupName } : d;
  });
}

export type ZipLayout = 'production' | 'unit' | 'flat';

/**
 * Download a ZIP of the selection.
 *
 * A single document short-circuits to its own signed download URL: the server
 * would happily build a one-entry archive, but handing someone a ZIP holding
 * one PDF is worse than handing them the PDF.
 */
export async function downloadDocuments(documentIds: string[], layout: ZipLayout): Promise<void> {
  if (documentIds.length === 0) return;

  if (documentIds.length === 1) {
    const url = await getSignedUrl(documentIds[0], 'download');
    window.open(url, '_blank', 'noopener');
    return;
  }

  const response = await fetch(`${API_BASE_URL}/v1/admin/documents/zip`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders() as Record<string, string>) },
    body: JSON.stringify({ document_ids: documentIds, layout }),
  });
  if (!response.ok) throw new Error(await errorMessage(response));

  // Streamed, so it is read as a blob rather than JSON.
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filenameFrom(response) ?? 'hollywood-trucks-documents.zip';
  a.click();
  // Revoked on a timer: revoking immediately can cancel the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface EmailResult {
  delivery: 'attachment' | 'link';
  documentCount: number;
  expiresAt?: string;
}

/** Email the selection; the server decides attachment vs link. */
export async function emailDocuments(
  documentIds: string[],
  recipientEmail: string,
  layout: ZipLayout,
): Promise<EmailResult> {
  return adminFetch<EmailResult>('/documents/email', {
    method: 'POST',
    body: JSON.stringify({ document_ids: documentIds, recipient_email: recipientEmail, layout }),
  });
}

export interface UploadInput {
  file: File;
  unitId: string;
  documentTypeId: string;
  customTypeLabel?: string;
  expirationDate?: string;
  /** Set when replacing a document that already exists for this unit + type. */
  supersedesId?: string;
}

/** Upload one document. multipart — adminFetch drops Content-Type for FormData. */
export async function uploadDocument(input: UploadInput): Promise<{ document: UnitDocument }> {
  const form = new FormData();
  form.append('file', input.file);
  form.append('unit_id', input.unitId);
  form.append('document_type_id', input.documentTypeId);
  if (input.customTypeLabel) form.append('custom_type_label', input.customTypeLabel);
  if (input.expirationDate) form.append('expiration_date', input.expirationDate);
  if (input.supersedesId) form.append('supersedes_id', input.supersedesId);

  return adminFetch<{ document: UnitDocument }>('/documents', { method: 'POST', body: form });
}

// ---------------------------------------------------------------------------
// Type catalogue + warning periods (T-009)
// ---------------------------------------------------------------------------

export interface DocumentTypeRecord {
  id: string;
  key: string;
  label: string;
  short_code: string | null;
  icon: string | null;
  is_required: boolean;
  has_expiry: boolean;
  sort_order: number;
  is_active: boolean;
  /** Shown to clients in the client portal; unset (older rows) means shown. */
  portal_visible?: boolean;
}

export async function fetchDocumentTypes(): Promise<DocumentTypeRecord[]> {
  const { types } = await adminFetch<{ types: DocumentTypeRecord[] }>('/documents/types');
  return types ?? [];
}

export async function createDocumentType(
  input: Partial<DocumentTypeRecord> & { key: string; label: string },
): Promise<DocumentTypeRecord> {
  const { type } = await adminFetch<{ type: DocumentTypeRecord }>('/documents/types', {
    method: 'POST', body: JSON.stringify(input),
  });
  return type;
}

/** `key` is deliberately absent — it is immutable and the API rejects it. */
export async function updateDocumentType(
  id: string,
  patch: Partial<Omit<DocumentTypeRecord, 'id' | 'key'>>,
): Promise<DocumentTypeRecord> {
  const { type } = await adminFetch<{ type: DocumentTypeRecord }>(`/documents/types/${id}`, {
    method: 'PATCH', body: JSON.stringify(patch),
  });
  return type;
}

/** Resolves `{ retired: true }` when documents reference the type. */
export async function deleteDocumentType(
  id: string,
): Promise<{ deleted?: boolean; retired?: boolean; referencingDocuments?: number }> {
  return adminFetch(`/documents/types/${id}`, { method: 'DELETE' });
}

export async function fetchWarningPeriods(): Promise<
  { id: string; company_id: string | null; document_type_id: string | null; warning_days: number }[]
> {
  const { periods } = await adminFetch<{ periods: [] }>('/documents/warning-periods');
  return periods ?? [];
}

export async function saveWarningPeriods(
  periods: { company_id: string | null; document_type_id: string | null; warning_days: number }[],
): Promise<void> {
  await adminFetch('/documents/warning-periods', { method: 'PUT', body: JSON.stringify({ periods }) });
}

/** Human byte total for panel headers and the selection bar. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** i;
  return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}

// ---------------------------------------------------------------------------
// T-144 PR#4 — document-share list / revoke (the management screen).
// ---------------------------------------------------------------------------

export type ShareStatus = 'active' | 'expired' | 'revoked';

export interface ShareRecipient {
  id: string;
  contact_name: string | null;
  role_key: string | null;
  email: string | null;
  created_at: string;
  last_downloaded_at: string | null;
  download_count: number;
  status: 'active' | 'revoked';
}

export interface ShareBundle {
  id: string;
  production_group_id: string;
  production_name: string | null;
  layout: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  status: ShareStatus;
  recipients: ShareRecipient[];
}

/** GET /admin/documents/share — every bundle with recipients + rollups. */
export async function fetchDocumentShares(): Promise<{ bundles: ShareBundle[] }> {
  return adminFetch<{ bundles: ShareBundle[] }>('/documents/share');
}

/**
 * POST /admin/documents/share/:id/revoke — revoke a whole bundle, or a single
 * recipient token when recipientId is given.
 */
export async function revokeDocumentShare(
  bundleId: string,
  recipientId?: string,
): Promise<{ ok: boolean; scope?: string; alreadyRevoked?: boolean }> {
  return adminFetch(`/documents/share/${bundleId}/revoke`, {
    method: 'POST',
    body: JSON.stringify(recipientId ? { recipient_id: recipientId } : {}),
  });
}
